import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Briefcase, FileText, Star, Wrench, Eye, Plus, Trash2, Save, X, Target } from "lucide-react";
import { createJob, updateJob } from "./thailandJobsApi.js";
import "./jobBuilder.css";

/**
 * The job builder - creating one position for one employer, at the same level
 * as the detailed worker profile dialog (`WorkerDetailedProfileDialog`): a
 * pinned header band, one row of tabs, a single scrolling panel, and a pinned
 * footer that owns the save. It opens over the งาน dialog rather than replacing
 * its body, because a job is a record in its own right, not a sub-panel of a
 * list.
 *
 * The field set and the tab split (ข้อมูลงาน / สวัสดิการและภาษา / ทักษะ /
 * ตรวจทาน) follow the reference builder in the separate `workmate/Front` repo
 * (`packages/ws-shared/src/placement/JobBuilderPage.jsx`), reduced to what the
 * real Create Job API actually stores. Deliberately NOT carried over from that
 * reference:
 *  - cover image: there is no job-image endpoint on this platform.
 *  - benefits/languages as picked master-data ids: this API stores each as one
 *    free-text field, so the chips here compose that text and the text stays
 *    editable. Rendering them as a closed picker would imply a vocabulary the
 *    server does not have.
 *  - age/gender/education requirements: the request has no field for any of
 *    them, and the API silently drops unknown keys.
 *
 * It never collects a workerId - creating a job records demand only, and no
 * matching, assignment or tracking is started by anything on this screen.
 */

const TABS = [
  { key: "general", label: "ข้อมูลงาน", Icon: FileText },
  { key: "perks", label: "สวัสดิการและภาษา", Icon: Star },
  { key: "skills", label: "ทักษะที่ต้องการ", Icon: Wrench },
  // P161: its own tab, because these are the fields the matching score reads -
  // burying them under "ข้อมูลงาน" would make them look optional decoration.
  { key: "matching", label: "เงื่อนไขการจับคู่", Icon: Target },
  { key: "preview", label: "ตรวจทาน", Icon: Eye },
];

const SALARY_PERIOD_OPTIONS = [
  { value: "Daily", label: "รายวัน" },
  { value: "Monthly", label: "รายเดือน" },
];

/**
 * Draft keeps the position off the recruiting list until someone marks it
 * ready; Open publishes the demand immediately. Those are the only two values
 * the create endpoint accepts.
 */
const JOB_STATUS_OPTIONS = [
  { value: "Draft", label: "ร่าง (ยังไม่เปิดรับ)" },
  { value: "Open", label: "เปิดรับทันที" },
];

const BENEFIT_SUGGESTIONS = ["ประกันสังคม", "ที่พัก", "อาหาร", "รถรับส่ง", "ค่าล่วงเวลา (OT)", "โบนัส", "ชุดทำงาน"];
const LANGUAGE_SUGGESTIONS = ["ภาษาไทยเบื้องต้น", "ภาษาไทยสื่อสารได้ดี", "ภาษาอังกฤษเบื้องต้น", "ภาษาพม่า", "ภาษากัมพูชา", "ภาษาลาว"];

const EMPTY_SKILL_ROW = { skillCode: "", skillName: "", minimumLevel: "", requiredCount: "" };

const EMPTY_FORM = {
  jobTitle: "",
  jobDescription: "",
  requiredWorkerCount: "",
  startDate: "",
  endDate: "",
  salaryAmount: "",
  salaryCurrency: "THB",
  salaryPeriod: "Monthly",
  jobStatus: "Draft",
  benefitsText: "",
  languageRequirementText: "",
  skillRequirements: [{ ...EMPTY_SKILL_ROW }],
  // P161: the structured requirements the matching score can actually compute
  // with. Each is backed by a real stored worker field - see payload 050.
  minYearsExperience: "",
  preferredNationalities: "",
  workProvince: "",
  maxExpectedSalaryAmount: "",
};

/**
 * P161: the nationalities this platform's workers actually have. Offered as
 * chips over a free-text field, the same way benefits and languages already
 * are, because the stored worker column is free text too - a closed picker here
 * would imply a vocabulary dbo.WmxWorkers does not have.
 */
const NATIONALITY_SUGGESTIONS = ["Myanmar", "Cambodia", "Laos", "Bangladesh", "Nepal", "Vietnam"];

/** P161: turns a stored job detail into this form's own shape. */
function formFromJob(job) {
  return {
    jobTitle: job.jobTitle ?? "",
    jobDescription: job.jobDescription ?? "",
    requiredWorkerCount: job.requiredWorkerCount == null ? "" : String(job.requiredWorkerCount),
    // The API returns a plain YYYY-MM-DD DateOnly; sliced rather than parsed,
    // because `new Date("2026-08-09")` is midnight UTC and renders as the day
    // before for anyone west of Greenwich.
    startDate: job.startDate ? String(job.startDate).slice(0, 10) : "",
    endDate: job.endDate ? String(job.endDate).slice(0, 10) : "",
    salaryAmount: job.salaryAmount == null ? "" : String(job.salaryAmount),
    salaryCurrency: job.salaryCurrency ?? "THB",
    salaryPeriod: job.salaryPeriod ?? "Monthly",
    jobStatus: job.jobStatus ?? "Draft",
    benefitsText: job.benefitsText ?? "",
    languageRequirementText: job.languageRequirementText ?? "",
    skillRequirements:
      (job.skillRequirements ?? []).length === 0
        ? [{ ...EMPTY_SKILL_ROW }]
        : job.skillRequirements.map((row) => ({
            requirementId: row.requirementId ?? null,
            skillCode: row.skillCode ?? "",
            skillName: row.skillName ?? "",
            minimumLevel: row.minimumLevel ?? "",
            requiredCount: row.requiredCount == null ? "" : String(row.requiredCount),
          })),
    minYearsExperience: job.minYearsExperience == null ? "" : String(job.minYearsExperience),
    preferredNationalities: job.preferredNationalities ?? "",
    workProvince: job.workProvince ?? "",
    maxExpectedSalaryAmount: job.maxExpectedSalaryAmount == null ? "" : String(job.maxExpectedSalaryAmount),
  };
}

const NOT_MATCHING_NOTE =
  "การสร้างงานนี้เป็นการบันทึกความต้องการแรงงานเพื่อรอ Matching ในขั้นตอนถัดไป ยังไม่ใช่การจับคู่แรงงาน ไม่สร้าง assignment และไม่รับ workerId ใด ๆ ในขั้นตอนนี้";

function describeCreateJobError(error) {
  if (error?.validation) {
    return "กรุณากรอกชื่อตำแหน่งงานและจำนวนที่ต้องการให้ครบถ้วน";
  }
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์สร้างงานให้นายจ้างรายนี้";
  }
  if (error?.status === 404) {
    return "ไม่พบนายจ้างรายนี้ อาจถูกลบหรือย้ายแล้ว";
  }
  if (error?.status === 409) {
    return "สถานะของนายจ้างรายนี้เปลี่ยนไปแล้ว กรุณารีเฟรชแล้วลองใหม่อีกครั้ง";
  }
  if (error?.status === 400) {
    return "ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบฟอร์มอีกครั้ง";
  }
  return "ไม่สามารถสร้างงานได้ กรุณาลองใหม่อีกครั้ง";
}

/**
 * The required marker is CSS generated content on the label, never an element
 * inside it: anything inside `<label>` lands in `label.textContent`, which is
 * the control's accessible name, so a literal "*" would rename the field to
 * "ชื่อตำแหน่งงาน *" and make it unfindable by the name a person reads. The
 * meaning for a screen reader rides on a visually-hidden sibling outside the
 * label. (Same trap the worker profile editor documents.)
 */
function Field({ label, required, hint, span, children }) {
  return (
    <div className={`wx-jb-field${span ? ` wx-jb-field--${span}` : ""}${required ? " wx-jb-field--required" : ""}`}>
      <label className="wx-jb-field-control">
        <span className="wx-jb-field-label">{label}</span>
        {children}
      </label>
      {required && <span className="wx-visually-hidden">ช่องที่จำเป็นต้องกรอก</span>}
      {hint && <span className="wx-jb-field-hint">{hint}</span>}
    </div>
  );
}

function FieldGroup({ Icon, children }) {
  return (
    <h4 className="wx-jb-group">
      {Icon && <Icon size={14} aria-hidden="true" />}
      {children}
    </h4>
  );
}

/**
 * Appends a suggestion to a free-text field instead of replacing it: the text
 * is what gets stored, so the chips have to build it rather than stand in for
 * a value the API cannot hold.
 */
function appendSuggestion(current, suggestion) {
  const parts = current
    .split(/[,\n]/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.includes(suggestion)) {
    return parts.filter((part) => part !== suggestion).join(", ");
  }
  return [...parts, suggestion].join(", ");
}

function hasSuggestion(current, suggestion) {
  return current
    .split(/[,\n]/)
    .map((part) => part.trim())
    .includes(suggestion);
}

function previewValue(value) {
  return value === "" || value == null ? "-" : value;
}

export default function JobBuilderDialog({ open, employer, agentId, onClose, onCreated, job }) {
  // P161: the same builder, opened on an existing position. `job` is the full
  // job detail; its absence means create. Editing reuses this screen rather than
  // getting one of its own, so the fields, the validation and the wording can
  // never drift between creating a position and changing it.
  const isEdit = Boolean(job?.jobId);
  const dialogRef = useRef(null);
  const [activeTab, setActiveTab] = useState("general");
  const [form, setForm] = useState(EMPTY_FORM);
  const [dirty, setDirty] = useState(false);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const [submitState, setSubmitState] = useState({ status: "idle", error: null });

  useEffect(() => {
    if (!open) {
      setActiveTab("general");
      setForm(EMPTY_FORM);
      setDirty(false);
      setConfirmingClose(false);
      setSubmitState({ status: "idle", error: null });
      return;
    }

    // Opening on an existing position fills every field from what is stored, so
    // the operator edits the record rather than retyping it.
    setActiveTab("general");
    setForm(job?.jobId ? formFromJob(job) : EMPTY_FORM);
    setDirty(false);
    setConfirmingClose(false);
    setSubmitState({ status: "idle", error: null });
  }, [open, job]);

  const requestClose = useCallback(() => {
    if (dirty) {
      setConfirmingClose(true);
      return;
    }
    onClose?.();
  }, [dirty, onClose]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        requestClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, requestClose]);

  function update(field, value) {
    setDirty(true);
    setForm((previous) => ({ ...previous, [field]: value }));
  }

  function updateSkillRow(index, field, value) {
    setDirty(true);
    setForm((previous) => ({
      ...previous,
      skillRequirements: previous.skillRequirements.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row)),
    }));
  }

  function addSkillRow() {
    setForm((previous) => ({ ...previous, skillRequirements: [...previous.skillRequirements, { ...EMPTY_SKILL_ROW }] }));
  }

  function removeSkillRow(index) {
    setDirty(true);
    setForm((previous) => ({
      ...previous,
      skillRequirements:
        previous.skillRequirements.length === 1 ? [{ ...EMPTY_SKILL_ROW }] : previous.skillRequirements.filter((_, rowIndex) => rowIndex !== index),
    }));
  }

  // The two the API refuses the request without - counted per tab so the tab
  // itself says what is still missing, the same way the worker profile does.
  const missingByTab = useMemo(() => {
    const generalMissing = (form.jobTitle.trim() ? 0 : 1) + (form.requiredWorkerCount.trim() ? 0 : 1);
    return { general: generalMissing, perks: 0, skills: 0, matching: 0, preview: 0 };
  }, [form.jobTitle, form.requiredWorkerCount]);

  const totalMissing = missingByTab.general;

  async function handleSubmit(event) {
    event?.preventDefault?.();
    const requiredWorkerCount = Number(form.requiredWorkerCount);
    if (!form.jobTitle.trim() || !form.requiredWorkerCount.trim() || !Number.isFinite(requiredWorkerCount) || requiredWorkerCount <= 0) {
      setSubmitState({ status: "error", error: { validation: true } });
      setActiveTab("general");
      return;
    }
    setSubmitState({ status: "submitting", error: null });
    // Every field this form collects, in the exact shape both endpoints take.
    // One object for create and update, so an edit can never silently stop
    // sending a field the create form still does.
    const payload = {
      jobTitle: form.jobTitle.trim(),
      jobDescription: form.jobDescription.trim() || null,
      requiredWorkerCount,
      startDate: form.startDate || null,
      endDate: form.endDate || null,
      salaryAmount: form.salaryAmount === "" ? null : Number(form.salaryAmount),
      salaryCurrency: form.salaryCurrency.trim() || null,
      salaryPeriod: form.salaryPeriod || null,
      benefitsText: form.benefitsText.trim() || null,
      languageRequirementText: form.languageRequirementText.trim() || null,
      jobStatus: form.jobStatus,
      skillRequirements: form.skillRequirements
        .filter((row) => row.skillCode.trim())
        .map((row) => ({
          // Only ever sent for a requirement that already exists, so it is
          // updated in place instead of being soft-deleted and re-inserted under
          // a new id. A brand-new row has no id and sends no key at all - a
          // create request has nothing to carry here.
          ...(row.requirementId ? { requirementId: row.requirementId } : {}),
          skillCode: row.skillCode.trim(),
          skillName: row.skillName.trim() || null,
          minimumLevel: row.minimumLevel.trim() || null,
          requiredCount: row.requiredCount.trim() ? Number(row.requiredCount) : null,
        })),
      // P161: the structured matching requirements. Blank stays null - "not
      // stated" is a real answer that judges nobody, and 0 would mean something
      // quite different.
      minYearsExperience: form.minYearsExperience.trim() ? Number(form.minYearsExperience) : null,
      preferredNationalities: form.preferredNationalities.trim() || null,
      workProvince: form.workProvince.trim() || null,
      maxExpectedSalaryAmount: form.maxExpectedSalaryAmount.trim() ? Number(form.maxExpectedSalaryAmount) : null,
    };

    try {
      if (isEdit) {
        await updateJob(job.jobId, payload);
      } else {
        await createJob({ ...payload, agentId: agentId ?? null, employerId: employer?.employerId ?? null });
      }
      setSubmitState({ status: "idle", error: null });
      setDirty(false);
      onCreated?.();
    } catch (error) {
      setSubmitState({ status: "error", error });
    }
  }

  if (!open) {
    return null;
  }

  const submitting = submitState.status === "submitting";

  return (
    <div
      className="wx-jb-root"
      // mousedown, not click: a drag that starts inside the form and ends on
      // the backdrop must not throw the form away.
      onMouseDown={(event) => {
        if (dialogRef.current && !dialogRef.current.contains(event.target)) {
          requestClose();
        }
      }}
    >
      <div className="wx-jb-backdrop" aria-hidden="true" />
      <div ref={dialogRef} className="wx-jb-dialog" role="dialog" aria-modal="true" aria-labelledby="wx-jb-title">
        <header className="wx-jb-head">
          <p className="wx-jb-kicker">
            <Briefcase size={16} aria-hidden="true" />
            {isEdit ? "แก้ไขตำแหน่งงาน" : "ตำแหน่งงานใหม่"}
          </p>
          <div className="wx-jb-head-main">
            <div className="wx-jb-head-id">
              <h2 id="wx-jb-title" className="wx-jb-title">
                {isEdit ? job.jobTitle || "แก้ไขงาน" : "สร้างงานใหม่"}
              </h2>
              <p className="wx-jb-sub">
                นายจ้าง <strong>{employer?.legalName ?? "-"}</strong>
                <span className="wx-jb-mono">{employer?.employerCode ?? "-"}</span>
              </p>
              <div className="wx-jb-pills">
                {!isEdit && <span className="wx-badge wx-badge--danger">ยังไม่ใช่การจับคู่แรงงาน</span>}
                <span className="wx-badge wx-badge--neutral">{form.jobStatus === "Open" ? "จะเปิดรับทันที" : "จะบันทึกเป็นร่าง"}</span>
              </div>
            </div>
            {/* The required-field count used to sit here as a bordered box
                beside the close button, which made a quiet fact look like an
                alarm. The tab that holds those fields already carries the
                count, and the footer says it again right beside Save - where
                it matters - so the header just holds the record's identity. */}
            <div className="wx-jb-head-aside">
              <button type="button" className="wx-jb-close" onClick={requestClose} aria-label="ปิดหน้าต่างสร้างงาน">
                <X size={17} aria-hidden="true" />
              </button>
            </div>
          </div>
        </header>

        <div className="wx-jb-tabs">
          <nav className="wx-jb-nav" aria-label="หัวข้อข้อมูลงาน">
            {TABS.map(({ key, label, Icon }) => {
              const count = missingByTab[key] ?? 0;
              const active = activeTab === key;
              return (
                <button
                  key={key}
                  type="button"
                  className={`wx-jb-nav-item${active ? " wx-jb-nav-item--active" : ""}`}
                  aria-current={active ? "true" : undefined}
                  aria-describedby={count > 0 ? `wx-jb-tabdesc-${key}` : undefined}
                  onClick={() => setActiveTab(key)}
                >
                  <Icon size={15} aria-hidden="true" />
                  <span>{label}</span>
                  {count > 0 && (
                    <span className="wx-jb-tab-count" aria-hidden="true">
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
          {/* The count rides in aria-describedby so it never renames the tab. */}
          <div className="wx-visually-hidden">
            {TABS.map(({ key }) =>
              (missingByTab[key] ?? 0) > 0 ? (
                <span key={key} id={`wx-jb-tabdesc-${key}`}>
                  ยังขาด {missingByTab[key]} ช่อง
                </span>
              ) : null,
            )}
          </div>
        </div>

        <div className="wx-jb-scroll">
          <form className="wx-jb-main" onSubmit={handleSubmit}>
            {activeTab === "general" && (
              <section className="wx-jb-section">
                <div className="wx-jb-grid">
                  <FieldGroup Icon={FileText}>รายละเอียดตำแหน่ง</FieldGroup>
                  <Field label="ชื่อตำแหน่งงาน" required span="wide">
                    <input
                      type="text"
                      placeholder="เช่น ช่างเย็บผ้าอุตสาหกรรม"
                      value={form.jobTitle}
                      onChange={(event) => update("jobTitle", event.target.value)}
                    />
                  </Field>
                  <Field label="รายละเอียดงาน" span="wide" hint="ลักษณะงาน กะการทำงาน และเงื่อนไขที่แรงงานควรรู้ก่อนตัดสินใจ">
                    <textarea
                      rows={4}
                      maxLength={2000}
                      placeholder="เช่น เย็บผ้าอุตสาหกรรมด้วยจักรอุตสาหกรรม กะกลางวัน 8 ชั่วโมง"
                      value={form.jobDescription}
                      onChange={(event) => update("jobDescription", event.target.value)}
                    />
                  </Field>
                  <Field label="จำนวนที่ต้องการ (อัตรา)" required>
                    <input
                      type="number"
                      min="1"
                      placeholder="เช่น 30"
                      value={form.requiredWorkerCount}
                      onChange={(event) => update("requiredWorkerCount", event.target.value)}
                    />
                  </Field>
                  <Field label="สถานะเริ่มต้น" hint="ร่างจะยังไม่ถูกนับเป็นตำแหน่งที่เปิดรับ">
                    <select value={form.jobStatus} onChange={(event) => update("jobStatus", event.target.value)}>
                      {JOB_STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <FieldGroup Icon={Briefcase}>ระยะเวลาและค่าจ้าง</FieldGroup>
                  <Field label="วันที่เริ่มงาน">
                    <input type="date" value={form.startDate} onChange={(event) => update("startDate", event.target.value)} />
                  </Field>
                  <Field label="วันที่สิ้นสุด">
                    <input type="date" value={form.endDate} onChange={(event) => update("endDate", event.target.value)} />
                  </Field>
                  <Field label="ค่าจ้าง">
                    <input
                      type="number"
                      min="0"
                      placeholder="เช่น 450"
                      value={form.salaryAmount}
                      onChange={(event) => update("salaryAmount", event.target.value)}
                    />
                  </Field>
                  <Field label="สกุลเงิน">
                    <input type="text" maxLength={8} value={form.salaryCurrency} onChange={(event) => update("salaryCurrency", event.target.value)} />
                  </Field>
                  <Field label="รอบการจ่าย">
                    <select value={form.salaryPeriod} onChange={(event) => update("salaryPeriod", event.target.value)}>
                      {SALARY_PERIOD_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
              </section>
            )}

            {activeTab === "perks" && (
              <section className="wx-jb-section">
                <div className="wx-jb-grid">
                  <FieldGroup Icon={Star}>สวัสดิการ</FieldGroup>
                  <div className="wx-jb-field wx-jb-field--wide">
                    <div className="wx-jb-chips">
                      {BENEFIT_SUGGESTIONS.map((suggestion) => (
                        <button
                          key={suggestion}
                          type="button"
                          className={`wx-jb-chip${hasSuggestion(form.benefitsText, suggestion) ? " wx-jb-chip--on" : ""}`}
                          aria-pressed={hasSuggestion(form.benefitsText, suggestion)}
                          onClick={() => update("benefitsText", appendSuggestion(form.benefitsText, suggestion))}
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                  <Field label="สวัสดิการที่ให้" span="wide" hint="กดปุ่มด้านบนเพื่อเติม หรือพิมพ์เพิ่มเองได้ — ระบบเก็บเป็นข้อความตามที่เห็น">
                    <textarea
                      rows={3}
                      maxLength={1000}
                      placeholder="เช่น ประกันสังคม ที่พัก อาหารกลางวัน"
                      value={form.benefitsText}
                      onChange={(event) => update("benefitsText", event.target.value)}
                    />
                  </Field>

                  <FieldGroup Icon={FileText}>ภาษาที่ต้องการ</FieldGroup>
                  <div className="wx-jb-field wx-jb-field--wide">
                    <div className="wx-jb-chips">
                      {LANGUAGE_SUGGESTIONS.map((suggestion) => (
                        <button
                          key={suggestion}
                          type="button"
                          className={`wx-jb-chip${hasSuggestion(form.languageRequirementText, suggestion) ? " wx-jb-chip--on" : ""}`}
                          aria-pressed={hasSuggestion(form.languageRequirementText, suggestion)}
                          onClick={() => update("languageRequirementText", appendSuggestion(form.languageRequirementText, suggestion))}
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                  <Field label="ความต้องการด้านภาษา" span="wide">
                    <textarea
                      rows={2}
                      maxLength={500}
                      placeholder="เช่น ภาษาไทยเบื้องต้น สื่อสารกับหัวหน้างานได้"
                      value={form.languageRequirementText}
                      onChange={(event) => update("languageRequirementText", event.target.value)}
                    />
                  </Field>
                </div>
              </section>
            )}

            {activeTab === "skills" && (
              <section className="wx-jb-section">
                <div className="wx-jb-repeat-head">
                  <FieldGroup Icon={Wrench}>ทักษะที่ต้องการ</FieldGroup>
                  <button type="button" className="wx-button wx-button--ghost wx-jb-add-skill" onClick={addSkillRow}>
                    <Plus size={14} aria-hidden="true" />
                    เพิ่มทักษะ
                  </button>
                </div>
                <p className="wx-jb-note">
                  แถวที่ไม่ได้กรอกรหัสทักษะจะไม่ถูกบันทึก — ผลรวมจำนวนของแต่ละทักษะไม่จำเป็นต้องเท่ากับจำนวนที่ต้องการทั้งหมด
                </p>
                <ul className="wx-jb-repeat-list">
                  {form.skillRequirements.map((row, index) => (
                    <li key={index} className="wx-jb-repeat-row">
                      <div className="wx-jb-grid">
                        <Field label="รหัสทักษะ">
                          <input
                            type="text"
                            placeholder="เช่น SEWING"
                            value={row.skillCode}
                            onChange={(event) => updateSkillRow(index, "skillCode", event.target.value)}
                          />
                        </Field>
                        <Field label="ชื่อทักษะ">
                          <input
                            type="text"
                            placeholder="เช่น เย็บผ้าอุตสาหกรรม"
                            value={row.skillName}
                            onChange={(event) => updateSkillRow(index, "skillName", event.target.value)}
                          />
                        </Field>
                        <Field label="ระดับขั้นต่ำ">
                          <input
                            type="text"
                            placeholder="เช่น ชำนาญ"
                            value={row.minimumLevel}
                            onChange={(event) => updateSkillRow(index, "minimumLevel", event.target.value)}
                          />
                        </Field>
                        <Field label="จำนวนที่ต้องการ">
                          <input
                            type="number"
                            min="0"
                            value={row.requiredCount}
                            onChange={(event) => updateSkillRow(index, "requiredCount", event.target.value)}
                          />
                        </Field>
                      </div>
                      <button
                        type="button"
                        className="wx-jb-repeat-remove"
                        onClick={() => removeSkillRow(index)}
                        aria-label={`ลบทักษะแถวที่ ${index + 1}`}
                      >
                        <Trash2 size={14} aria-hidden="true" />
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {activeTab === "matching" && (
              <section className="wx-jb-section">
                <p className="wx-jb-note">
                  ช่องในหน้านี้คือเงื่อนไขที่ระบบใช้คิดคะแนนความเหมาะสมกับโปรไฟล์แรงงาน — ทุกช่องเทียบกับข้อมูลที่แรงงานบันทึกไว้จริง
                  ช่องที่เว้นว่างจะไม่ถูกนำมาตัดสินใคร
                </p>
                <div className="wx-jb-grid">
                  <FieldGroup Icon={Target}>คุณสมบัติที่ต้องการ</FieldGroup>
                  <Field
                    label="ประสบการณ์ขั้นต่ำ (ปี)"
                    hint="เทียบกับจำนวนปีประสบการณ์ในโปรไฟล์แรงงาน — แรงงานที่ยังไม่ได้บันทึกจะไม่ถูกตัดสิน"
                  >
                    <input
                      type="number"
                      min="0"
                      max="60"
                      placeholder="เช่น 2"
                      value={form.minYearsExperience}
                      onChange={(event) => update("minYearsExperience", event.target.value)}
                    />
                  </Field>
                  <Field
                    label="ค่าจ้างสูงสุดที่รับได้ (บาท)"
                    hint="ถ้าแรงงานคาดหวังค่าจ้างสูงกว่านี้ ระบบจะเตือนไว้ก่อน ไม่ใช่ไปรู้ตอนยื่นข้อเสนอ"
                  >
                    <input
                      type="number"
                      min="0"
                      placeholder="เช่น 18000"
                      value={form.maxExpectedSalaryAmount}
                      onChange={(event) => update("maxExpectedSalaryAmount", event.target.value)}
                    />
                  </Field>
                  <Field
                    label="จังหวัดที่ทำงาน"
                    span="wide"
                    hint="เทียบกับจังหวัดที่แรงงานอยากไปทำงาน — ถ้างานนี้ยังไม่ได้ผูกกับหน้างาน ช่องนี้คือสิ่งเดียวที่บอกได้ว่างานอยู่ที่ไหน"
                  >
                    <input
                      type="text"
                      maxLength={120}
                      placeholder="เช่น สมุทรปราการ"
                      value={form.workProvince}
                      onChange={(event) => update("workProvince", event.target.value)}
                    />
                  </Field>

                  <FieldGroup Icon={Star}>สัญชาติที่ต้องการ</FieldGroup>
                  <div className="wx-jb-field wx-jb-field--wide">
                    <div className="wx-jb-chips">
                      {NATIONALITY_SUGGESTIONS.map((suggestion) => (
                        <button
                          key={suggestion}
                          type="button"
                          className={`wx-jb-chip${hasSuggestion(form.preferredNationalities, suggestion) ? " wx-jb-chip--on" : ""}`}
                          aria-pressed={hasSuggestion(form.preferredNationalities, suggestion)}
                          onClick={() => update("preferredNationalities", appendSuggestion(form.preferredNationalities, suggestion))}
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                  <Field
                    label="สัญชาติที่ต้องการ"
                    span="wide"
                    hint="เป็นความต้องการเพื่อใช้เรียงลำดับเท่านั้น ไม่ใช่การคัดออก — แรงงานสัญชาติอื่นยังขึ้นในรายชื่อ พร้อมหมายเหตุกำกับ"
                  >
                    <input
                      type="text"
                      maxLength={300}
                      placeholder="เช่น Myanmar, Cambodia"
                      value={form.preferredNationalities}
                      onChange={(event) => update("preferredNationalities", event.target.value)}
                    />
                  </Field>
                </div>
              </section>
            )}

            {activeTab === "preview" && (
              <section className="wx-jb-section">
                <div className="wx-jb-preview">
                  <PreviewRow label="นายจ้าง" value={employer?.legalName} />
                  <PreviewRow label="ชื่อตำแหน่งงาน" value={form.jobTitle} />
                  <PreviewRow label="จำนวนที่ต้องการ" value={form.requiredWorkerCount ? `${form.requiredWorkerCount} อัตรา` : ""} />
                  <PreviewRow label="สถานะเริ่มต้น" value={JOB_STATUS_OPTIONS.find((option) => option.value === form.jobStatus)?.label} />
                  <PreviewRow label="ช่วงเวลา" value={[form.startDate, form.endDate].filter(Boolean).join(" - ")} />
                  <PreviewRow
                    label="ค่าจ้าง"
                    value={
                      form.salaryAmount
                        ? `${Number(form.salaryAmount).toLocaleString("th-TH")} ${form.salaryCurrency} ${
                            SALARY_PERIOD_OPTIONS.find((option) => option.value === form.salaryPeriod)?.label ?? ""
                          }`.trim()
                        : ""
                    }
                  />
                  <PreviewRow label="สวัสดิการ" value={form.benefitsText} />
                  <PreviewRow label="ภาษาที่ต้องการ" value={form.languageRequirementText} />
                  <PreviewRow
                    label="ทักษะที่ต้องการ"
                    value={form.skillRequirements
                      .filter((row) => row.skillCode.trim())
                      .map((row) => `${row.skillName.trim() || row.skillCode.trim()}${row.requiredCount ? ` ${row.requiredCount} อัตรา` : ""}`)
                      .join(", ")}
                  />
                  <PreviewRow label="ประสบการณ์ขั้นต่ำ" value={form.minYearsExperience ? `${form.minYearsExperience} ปี` : ""} />
                  <PreviewRow label="จังหวัดที่ทำงาน" value={form.workProvince} />
                  <PreviewRow label="สัญชาติที่ต้องการ" value={form.preferredNationalities} />
                  <PreviewRow
                    label="ค่าจ้างสูงสุดที่รับได้"
                    value={form.maxExpectedSalaryAmount ? `${Number(form.maxExpectedSalaryAmount).toLocaleString("th-TH")} บาท` : ""}
                  />
                  <PreviewRow label="รายละเอียดงาน" value={form.jobDescription} stack />
                </div>
                <div className="wx-jb-notice">{NOT_MATCHING_NOTE}</div>
              </section>
            )}
          </form>
        </div>

        <footer className={`wx-jb-footer${confirmingClose ? " wx-jb-footer--confirm" : ""}`}>
          {confirmingClose ? (
            <>
              <p className="wx-jb-confirm-text" role="alert">
                ยังมีข้อมูลงานที่ยังไม่ได้บันทึก ถ้าปิดตอนนี้ข้อมูลที่กรอกไว้จะหายไป
              </p>
              <div className="wx-jb-footer-actions">
                <button type="button" className="wx-button wx-button--ghost" onClick={() => onClose?.()}>
                  ปิดโดยไม่บันทึก
                </button>
                <button type="button" className="wx-button wx-button--primary" onClick={() => setConfirmingClose(false)}>
                  กลับไปกรอกต่อ
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="wx-jb-footer-status" role="status">
                {submitState.status === "error" && <span className="wx-jb-error">{describeCreateJobError(submitState.error)}</span>}
                {submitState.status === "idle" && dirty && (
                  <span className={totalMissing > 0 ? "wx-jb-dirty" : "wx-jb-ready"}>
                    {totalMissing > 0 ? `ยังขาดอีก ${totalMissing} ช่องที่จำเป็น` : "กรอกครบแล้ว พร้อมบันทึก"}
                  </span>
                )}
              </div>
              <div className="wx-jb-footer-actions">
                <button type="button" className="wx-button wx-button--ghost" onClick={requestClose}>
                  ปิด
                </button>
                <button type="button" className="wx-button wx-button--primary wx-jb-save" onClick={handleSubmit} disabled={submitting}>
                  <Save size={16} aria-hidden="true" />
                  {submitting
                    ? "กำลังบันทึก..."
                    : isEdit
                      ? "บันทึกการแก้ไข"
                      : form.jobStatus === "Open"
                        ? "สร้างงานและเปิดรับ"
                        : "สร้างงาน (Draft)"}
                </button>
              </div>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}

function PreviewRow({ label, value, stack }) {
  return (
    <div className={`wx-jb-preview-row${stack ? " wx-jb-preview-row--stack" : ""}`}>
      <span>{label}</span>
      <strong>{previewValue(value)}</strong>
    </div>
  );
}
